import { useMemo } from "react";

import { Accession, ApiService, Case } from "pwapi";

import { displayPriority } from "shared/utils/caseUtils";
import { billingTypeEnumToLabel } from "shared/utils/caseUtils";

import { useSelectedLabStation } from "hooks/useSelectedLabStation";

import {
  ClinicOption,
  mapClinicsToOptions,
  mapUsersToOptions,
  UserOption,
} from "./SelectSearchOptions";

const useCaseInputs = () => {
  const labStation = useSelectedLabStation();

  const inputs = useMemo(() => {
    return {
      priority: {
        id: "case__priority",
        name: "priority",
        label: "Priority",
        type: "select",
        options: ["normal", "stat", "rush"].map((priority) => ({
          label: displayPriority(priority),
          value: priority,
        })),
      },
      grossedByExternalLab: {
        id: "grossed_by_external_lab_id",
        name: "grossed_by_external_lab_id",
        label: "External Gross At",
        type: "select",
      },
      ezdermOrderId: {
        id: "case__external_ezderm_order_id",
        name: "external_ezderm_order_id",
        label: "EZDerm Order Number",
      },
      externalOrderId: {
        id: "case__external_order_id",
        name: "external_order_id",
        label: "ModMed Order Number",
      },
      collectedDate: {
        id: "case__collected_date",
        name: "collected_date",
        label: "Collected Date",
        type: "datetime-local",
        max: "9999-12-31T23:59",
      },
      receivedDate: {
        id: "case__received_date",
        name: "received_date",
        label: "Received Date",
        type: "datetime-local",
        max: "9999-12-31T23:59",
      },
      clinicId: {
        id: "case__clinic_id",
        name: "clinic",
        mapToOptions: mapClinicsToOptions,
        component: ClinicOption,
        serviceFunction: (args: Parameters<typeof ApiService.listLisClinics>) =>
          ApiService.listLisClinics({
            parentLookupLabStationId: labStation?.id,
            isArchived: false,
            ...args,
          }),
        label: "Clinic",
        filter: "omni",
      },
      orderingDoctorId: {
        id: "case__ordering_doctor_id",
        name: "ordering_doctor",
        component: UserOption,
        mapToOptions: mapUsersToOptions,
        serviceFunction: (args: Parameters<typeof ApiService.listLisUsers>) =>
          ApiService.listLisUsers({
            parentLookupLabStationId: labStation?.id,
            ...args,
          }),
        label: "Ordering Provider",
        filter: "name",
        filters: { userprofileUserType: "physician" },
      },
      supervisingPhysicianId: {
        id: "case__supervising_physician_id",
        name: "supervising_physician",
        component: UserOption,
        mapToOptions: mapUsersToOptions,
        serviceFunction: (args: Parameters<typeof ApiService.listLisUsers>) =>
          ApiService.listLisUsers({
            parentLookupLabStationId: labStation?.id,
            ...args,
          }),
        label: "Supervising Physician",
        filter: "name",
        filters: { userprofileUserType: "physician" },
      },
      billingType: {
        id: "case__billing_type",
        name: "billing_type",
        type: "select",
        label: "Billing Type",
        options: Object.values(Case.billing_type)
          .filter((b) =>
            (b as Case.billing_type | "cash") === "cash" ? false : true
          )
          .map((billing_type) => ({
            label: billingTypeEnumToLabel(billing_type),
            value: billing_type,
          })),
      },
      caseType: {
        id: "case__case_type",
        name: "case_type",
        label: "Case Type",
        type: "select",
        options: [
          { label: "", value: Accession.case_type.CLINICAL },
          { label: "Consult", value: Accession.case_type.CONSULT },
          { label: "TC-PC Split", value: Accession.case_type.TC_PC },
          { label: "Historical", value: Accession.case_type.HISTORICAL },
          { label: "Mohs QC", value: Accession.case_type.MOHS_QC },
          { label: "Triage", value: Accession.case_type.TRIAGE },
          { label: "Test", value: Accession.case_type.TEST },
          { label: "Education", value: Accession.case_type.EDUCATION },
        ],
      },
      billingProperties: [
        {
          name: "self_pay",
          label: "Self Pay",
          value: "self_pay",
          type: "radio",
        },
        {
          name: "self_pay_with_attestation",
          label: "Self Pay With Attestation",
          value: "self_pay_with_attestation",
          type: "radio",
        },
        {
          name: "indigent",
          label: "Indigent",
          value: "indigent",
          type: "radio",
        },
      ],
      billingComponent: {
        tcOnly: {
          id: "case__billing_component_tc_only",
          name: "billing_component_tc",
          label: "TC Only",
          value: "tc_only",
          type: "radio",
        },
        pcOnly: {
          id: "case__billing_component_pc",
          name: "billing_component_pc",
          label: "PC Only",
          value: "pc_only",
          type: "radio",
        },
      },
      externalAccession: {
        id: "case__external_accession",
        name: "external_accession",
        label: "External Accession",
      },
      specimenBagBarcode: {
        id: "specimen_bag_barcode",
        name: "specimen_bag_barcode",
        label: "Specimen Bag",
      },
    };
  }, [labStation?.id]);

  return inputs;
};

export default useCaseInputs;
